/**
 * LOS DOS ÁRBOLES DEL DRAFT, EN UN FICHERO QUE SE PUEDE MANDAR.
 * =============================================================================
 *
 * PARA QUÉ EXISTE. Quien tiene que dar el visto bueno a la colocación no entra
 * en la consola de migración: vive detrás del backoffice y del rol de
 * administrador. Hasta ahora la única forma de enseñarles el árbol era una
 * captura de pantalla del trozo que cupiera en el recuadro. Esto saca el dibujo
 * ENTERO —los dos árboles— en un solo `.html` que se manda por WhatsApp o por
 * correo y se abre en cualquier navegador, sin instalar nada y sin conexión.
 *
 * POR QUÉ HTML Y NO UNA IMAGEN. Una imagen de 40 nodos sale ilegible o enorme, y
 * no hay término medio: o se lee el nombre y no cabe el árbol, o cabe el árbol y
 * no se lee nada. Un HTML se amplía con el zoom del navegador, se busca un
 * nombre con Ctrl+F, se copia el texto, y con Ctrl+P sale un PDF presentable sin
 * que nadie tenga que convertir nada. Encima se genera con las mismas cajas y el
 * mismo CSS que ya usa la pantalla: no hay que rasterizar, que era la parte
 * frágil.
 *
 * SE CONSTRUYE DESDE EL BOSQUE, NO DESDE EL DOM DE LA CONSOLA. Serializar la
 * pantalla arrastraría las clases de Bootstrap y los estados de la aplicación,
 * que fuera de ella no significan nada —el fichero se vería roto—, y además
 * saldría con lo que estuviera PLEGADO plegado y con el scroll recortando el
 * resto. Partiendo de `buildDraftForest` sale siempre EXPANDIDO, con todos los
 * nodos, y es una función pura de bosque a cadena: se puede ejecutar con node
 * sobre un draft de mentira y comprobar lo que produce, sin navegador.
 *
 * EL FICHERO ES AUTOSUFICIENTE, y eso manda sobre cómo está escrito:
 *
 *   · TODO EL CSS VA DENTRO, en un `<style>` propio. Nada de clases de la
 *     aplicación: la hoja de estilos no viaja con el adjunto.
 *   · CERO JAVASCRIPT. No es una preferencia estética: los dos árboles tienen
 *     que verse aunque el navegador de turno bloquee scripts, así que el
 *     contenido no puede depender de ninguno. Los dos van maquetados uno detrás
 *     de otro y el zoom lo pone el navegador, que ya lo trae.
 *   · NADA REMOTO — ni fuentes, ni iconos, ni CDNs. La familia es un stack del
 *     sistema y el ancla es un carácter Unicode, no un icono de FontAwesome.
 *
 * LA CABECERA EXPLICA EL DIBUJO. Es lo único que llega con el fichero: qué es,
 * de qué run sale, la fecha, cuántos miembros y qué significa cada marca. Si no
 * está ahí dentro, para quien lo recibe no está.
 */

import {
  buildDraftForest,
  buildMmiForest,
  DraftForest,
  DraftSide,
  DraftTreeItem,
  DraftTreeMode,
} from "./draftTree";
import { MigrationDraft, MigrationMmiDraft } from "./types";

export interface DraftExportMeta {
  /** `draft.generatedFor`, que es `"<runId> (<quien lo resolvió>)"`. */
  runLabel: string;
  /** Cuándo se generó ESTE fichero. */
  generatedAt: number;
  /** El instante de la migración que declara el draft. */
  migrationDate: number;
  /** Ápice del árbol (`draft.root`). */
  root: string;
}

// -- escapado ---------------------------------------------------------------

/**
 * TODO LO QUE VIENE DEL DRAFT PASA POR AQUÍ. Los nombres son datos, y un dato
 * con un `<` dentro rompería el documento entero: la mitad del árbol dejaría de
 * verse y nadie sabría por qué.
 */
function esc(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// -- fechas -----------------------------------------------------------------

function fmtDate(ms: number | undefined | null): string {
  if (!ms || !isFinite(ms)) return "—";
  const d = new Date(ms);
  const p = (n: number): string => String(n).padStart(2, "0");
  return (
    `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ` +
    `${p(d.getHours())}:${p(d.getMinutes())}`
  );
}

/** `2026-07-25`, para el nombre del fichero. */
function isoDay(ms: number): string {
  const d = new Date(ms);
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * EL NOMBRE DEL FICHERO DICE QUÉ ES Y DE QUÉ RUN SALE.
 *
 * Va a acabar en el teléfono de alguien, entre otros veinte adjuntos, y puede
 * que se mire dos días después. `arboles.html` no vale: hay un draft por run y
 * se regenera entero cada vez que se toca un ancla. El run va en el nombre para
 * que no se pueda aprobar el dibujo de otro.
 */
export function draftExportBaseName(
  generatedFor: string,
  generatedAt: number,
  what = "arboles-colocacion"
): string {
  const run = (generatedFor || "run")
    .split(" (")[0]
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `gtr-${what}-${run || "run"}-${isoDay(generatedAt)}`;
}

// -- las tarjetas y las ramas ----------------------------------------------

interface ChildSlot {
  side: DraftSide;
  item: DraftTreeItem | null;
}

/**
 * EL MISMO REPARTO QUE HACE LA PANTALLA (`DraftTreeSubtree.slots`), y tiene que
 * serlo: si aquí un hijo derecho único se dibujara centrado bajo su padre, el
 * fichero que se manda diría una colocación distinta de la que enseña la
 * consola. Izquierda primero, y la pierna que falta ocupa su hueco igual.
 */
function slotsOf(item: DraftTreeItem, mode: DraftTreeMode): ChildSlot[] {
  if (mode !== "binary") {
    return item.children.map((child) => ({ side: null, item: child }));
  }
  const out: ChildSlot[] = [];
  for (const side of ["left", "right"] as const) {
    const own = item.children.filter((child) =>
      side === "left" ? child.side === "left" : child.side !== "left"
    );
    if (own.length) own.forEach((child) => out.push({ side, item: child }));
    else out.push({ side, item: null });
  }
  return out;
}

/**
 * LA TARJETA, DESDE LA MISMA FUENTE QUE LA PANTALLA.
 *
 * Todo lo que dice (el nombre comercial del paquete, el patrocinador, el pase)
 * viene ya resuelto en `item.card`, que arma `draftTree.ts`. Nada se recalcula
 * aquí: si el fichero exportado dedujera por su cuenta lo que la pantalla
 * deduce, tarde o temprano dirían cosas distintas y la revisión valdría cero.
 */
function renderCard(item: DraftTreeItem): string {
  const card = item.card;
  const classes = ["card"];
  if (card.isAnchor) classes.push("anchor");
  if (card.isRoot) classes.push("root");
  if (card.isPassUp) classes.push("passup");

  const out: string[] = [];
  if (item.side) {
    out.push(
      `<span class="side side-${item.side}">${
        item.side === "left" ? "I" : "D"
      }</span>`
    );
  }
  // EL PASE, arriba a la derecha: una flecha hacia arriba, porque es
  // literalmente lo que pasó — esta persona subió por encima de quien la
  // patrocinó. Nunca choca con la D del binario: el CMMI no tiene lados.
  if (card.isPassUp) {
    out.push(`<span class="passup-mark" title="pase (pass-up)">↑</span>`);
  }
  out.push(
    `<div class="name">${
      card.isAnchor ? '<span class="anchor-mark">⚓</span> ' : ""
    }${esc(card.name)}</div>`
  );
  out.push(`<div class="pkg">${esc(card.sub)}</div>`);

  if (card.detail) {
    out.push(
      `<div class="spon"><span class="of">${esc(
        card.detail.prefix
      )}</span> ${esc(card.detail.value)}${
        card.detail.warn
          ? `<span class="warn"${
              card.detail.title ? ` title="${esc(card.detail.title)}"` : ""
            }>*</span>`
          : ""
      }</div>`
    );
  }

  if (item.descendants) {
    out.push(`<span class="count">${item.descendants}</span>`);
  }

  return `<div class="${classes.join(" ")}">${out.join("")}</div>`;
}

function renderItem(item: DraftTreeItem, mode: DraftTreeMode): string {
  const out = [`<div class="node">`, renderCard(item)];
  if (item.children.length) {
    out.push(`<div class="kids">`);
    for (const slot of slotsOf(item, mode)) {
      out.push(`<div class="slot${slot.item ? "" : " slot-empty"}">`);
      out.push(
        slot.item
          ? renderItem(slot.item, mode)
          : `<div class="free">${slot.side === "left" ? "I" : "D"}</div>`
      );
      out.push(`</div>`);
    }
    out.push(`</div>`);
  }
  out.push(`</div>`);
  return out.join("");
}

// -- una sección: un árbol entero + lo que ese árbol no dibuja --------------

function renderForest(forest: DraftForest): string {
  const out: string[] = [];

  out.push(`<div class="chart">${renderItem(forest.root, forest.mode)}</div>`);

  // NADA SE DESCARTA. Un miembro que desaparece de la revisión sin que nadie lo
  // mencione es exactamente el fallo que este fichero viene a evitar.
  if (forest.duplicateLegs.length) {
    out.push(
      `<p class="alert"><strong>Dos miembros en la misma pierna del mismo ` +
        `padre</strong> — el borrador está corrupto y no debe aplicarse: ` +
        `${esc(forest.duplicateLegs.join(" · "))}</p>`
    );
  }
  if (forest.detached.length) {
    out.push(
      `<div class="detached"><p class="detached-title">${
        forest.detached.length
      } ${
        forest.detached.length === 1 ? "rama suelta" : "ramas sueltas"
      } — cuelgan de alguien que no está en el borrador. Se dibujan aparte ` +
        `porque existen, no porque estén bien.</p>` +
        `<div class="chart">${forest.detached
          .map((item) => renderItem(item, forest.mode))
          .join("")}</div></div>`
    );
  }
  if (forest.offTree.length) {
    out.push(
      `<p class="note"><strong>${forest.offTree.length} fuera del binario</strong> ` +
        `(sólo cuentan en el árbol de patrocinio, no ocupan pierna): ` +
        forest.offTree.map((n) => esc(n.label)).join(", ") +
        `</p>`
    );
  }
  out.push(
    `<p class="note">${forest.drawn} de ${forest.total} miembros dibujados · ` +
      `${forest.maxDepth} niveles de profundidad` +
      (forest.mode === "cmmi"
        ? ` · <strong>${forest.passUps} ${
            forest.passUps === 1 ? "pase" : "pases"
          }</strong>`
        : "") +
      `</p>`
  );

  return out.join("");
}

// -- el CSS del fichero -----------------------------------------------------
//
// Copiado en espíritu de `DraftTreeSubtree.vue` y REESCRITO aquí a propósito: el
// adjunto no puede depender de la hoja de estilos de la aplicación ni de
// Bootstrap. Todo lo del árbol va en `em` y cuelga del `font-size` de `.tree`,
// así que el zoom del navegador (y el `@media print`) lo escalan entero.

const STYLES = `
:root { color-scheme: light; }
* { box-sizing: border-box; }
body {
  margin: 0;
  padding: 1.5rem;
  background: #fff;
  color: #212529;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  font-size: 15px;
  line-height: 1.45;
}
h1 { font-size: 1.5rem; margin: 0 0 .35rem; }
h2 { font-size: 1.15rem; margin: 0 0 .2rem; }
p { margin: 0 0 .5rem; }
.lead { color: #495057; margin-bottom: .9rem; }
.meta {
  margin: 0 0 1rem;
  padding: .6rem .8rem;
  border: 1px solid #e9ecef;
  border-radius: .4rem;
  background: #f8f9fa;
  font-size: .9rem;
}
.meta div { margin: .1rem 0; }
.meta strong { color: #212529; }
.legend {
  margin: 0 0 1.4rem;
  padding: .6rem .8rem;
  border: 1px solid #e9ecef;
  border-radius: .4rem;
  font-size: .9rem;
}
.legend ul { margin: .3rem 0 0; padding-left: 1.1rem; }
.legend li { margin: .15rem 0; }
.legend .k {
  display: inline-block;
  min-width: 1.3em; height: 1.3em; line-height: 1.3em;
  border-radius: 50%; text-align: center;
  color: #fff; font-weight: 700; font-size: .8em;
}
.k-l { background: #0d6efd; }
.k-r { background: #198754; }
.k-a {
  background: #fff9e6; color: #997404;
  border: 2px solid #ffc107; border-radius: .25em;
}
.k-p { background: #6f42c1; }
section { margin-bottom: 2.5rem; }
.blurb { color: #495057; font-size: .92rem; margin-bottom: .9rem; }
.note { color: #6c757d; font-size: .85rem; margin: .6rem 0 0; }
.alert {
  margin: .8rem 0 0; padding: .4rem .6rem;
  border: 1px solid #f5c2c7; border-radius: .3rem;
  background: #f8d7da; color: #842029; font-size: .88rem;
}
.detached { margin-top: 1.6rem; padding-top: .8rem; border-top: 2px dashed #dc3545; }
.detached-title { color: #dc3545; font-size: .85rem; margin-bottom: .8rem; }

/* ---------- el árbol ---------- */
.tree { overflow-x: auto; font-size: 12px; padding: 1rem .25rem .5rem; }
.chart { display: flex; justify-content: flex-start; gap: 2em; min-width: min-content; }
.node { display: flex; flex-direction: column; align-items: center; }
/* Más ancha que la tarjeta de la pantalla (10em) A PROPÓSITO: aquí no hay zoom
   ni tooltip que valga —el fichero se mira como salga, muchas veces en un
   móvil—, y un nombre cortado con puntos suspensivos en el renglón del
   patrocinador deja la tarjeta sin decir de quién es. */
.card {
  position: relative;
  width: 11.5em;
  padding: .45em .5em .55em;
  border: 1px solid #dee2e6; border-radius: .4em;
  background: #fff; text-align: center; line-height: 1.25;
}
.card.anchor { border: 2px solid #ffc107; background: #fff9e6; }
/* EL PASE: morado, un color que no usa ningún otro árbol. Es la excepción del
   CMMI y tiene que distinguirse de un vistazo de todo lo demás. */
.card.passup { border: 2px solid #6f42c1; background: #f5f1fc; }
.card.root { border-color: #6c757d; background: #f8f9fa; }
.anchor-mark { color: #997404; }
.passup-mark {
  position: absolute; top: -.6em; right: -.55em;
  width: 1.5em; height: 1.5em; line-height: 1.5em;
  border-radius: 50%; background: #6f42c1; color: #fff;
  font-size: .85em; font-weight: 700; text-align: center;
}
.name {
  font-weight: 700; font-size: 1.05em;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.pkg {
  font-size: .95em; color: #6c757d;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
/* El patrocinador: mismo cuerpo que el paquete y NO más pequeño — se lee muchas
   veces seguidas. Lo discreto lo pone el color, no el tamaño. */
.spon {
  font-size: .95em; color: #868e96;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.spon .of { color: #adb5bd; }
.spon .warn { color: #dc3545; font-weight: 700; }
.side {
  position: absolute; top: -.6em;
  width: 1.5em; height: 1.5em; line-height: 1.5em;
  border-radius: 50%; color: #fff;
  font-size: .85em; font-weight: 700; text-align: center;
}
.side-left { left: -.55em; background: #0d6efd; }
.side-right { right: -.55em; background: #198754; }
/* Cuántos cuelgan por debajo. En la consola es el botón de plegar; aquí no hay
   nada que plegar, así que se queda sólo el número. */
.count {
  position: absolute; left: 50%; bottom: -.75em; transform: translateX(-50%);
  min-width: 1.6em; padding: 0 .35em;
  border: 1px solid #ced4da; border-radius: .8em; background: #fff;
  color: #adb5bd; font-size: .75em; font-weight: 700; line-height: 1.5;
}

/* ---------- los conectores ---------- */
.kids { position: relative; display: flex; justify-content: center; padding-top: 1.5em; }
.kids::before {
  content: ""; position: absolute; top: 0; left: 50%;
  height: 1.5em; border-left: 1px solid #ced4da;
}
/* El hueco lateral es .75em y no .3em como en la pantalla POR LAS MEDALLAS DEL
   LADO: sobresalen media letra por fuera de la tarjeta, y con dos hermanos
   pegados —lo normal en el árbol de patrocinio, donde caben muchos directos— la
   I de uno se monta sobre la D del vecino. En la consola casi no se nota porque
   hay zoom; aquí el fichero se mira como salga. */
.slot {
  position: relative; display: flex; flex-direction: column; align-items: center;
  padding: 1.5em .75em 0;
}
.slot::before, .slot::after {
  content: ""; position: absolute; top: 0; right: 50%;
  width: 50%; height: 1.5em; border-top: 1px solid #ced4da;
}
.slot::after { right: auto; left: 50%; border-left: 1px solid #ced4da; }
.slot:first-child::before, .slot:last-child::after { border: 0 none; }
.slot:first-child::after { border-radius: .35em 0 0 0; }
.slot:last-child::before { border-right: 1px solid #ced4da; border-radius: 0 .35em 0 0; }
.slot:only-child { padding-top: 0; }
.slot:only-child::before, .slot:only-child::after { display: none; }

/* La pierna libre ocupa el ancho de una tarjeta aunque el recuadro sea pequeño:
   así el padre queda centrado y un hijo único no parece estar en medio. */
.slot-empty { min-width: 13em; }
.free {
  display: flex; align-items: center; justify-content: center;
  width: 2.6em; height: 2.6em;
  border: 1px dashed #ced4da; border-radius: .4em;
  background: #fff; color: #adb5bd; font-weight: 700;
}

/* ---------- impresión ---------- */
/* El objetivo es que Ctrl+P dé un PDF presentable sin tocar nada. Apaisado
   porque un árbol es ancho, letra más pequeña para que quepan más niveles, y
   ninguna tarjeta partida por la mitad entre dos páginas. */
@page { size: landscape; margin: 10mm; }
@media print {
  body { padding: 0; font-size: 12px; }
  .tree { overflow: visible; font-size: 8.5px; }
  .card, .node, .free { break-inside: avoid; page-break-inside: avoid; }
  .meta, .legend { break-inside: avoid; page-break-inside: avoid; }
  section { break-inside: auto; }
  section + section { break-before: page; page-break-before: always; }
  .screen-only { display: none; }
}
`;

// -- el documento -----------------------------------------------------------

/** Un árbol dentro del fichero: su título, su explicación y su bosque. */
export interface DraftExportSection {
  title: string;
  blurb: string;
  forest: DraftForest;
}

/** Las líneas de la leyenda, según qué árboles lleve el fichero. */
function legendFor(sections: DraftExportSection[]): string {
  const modes = new Set(sections.map((s) => s.forest.mode));
  const li: string[] = [];

  if (modes.has("binary") || modes.has("unilevel")) {
    li.push(
      `<li><span class="k k-l">I</span> <span class="k k-r">D</span> la ` +
        `pierna: izquierda o derecha. En el árbol binario dice DÓNDE cuelga; ` +
        `en el de patrocinio, a qué pierna de su patrocinador le suman sus ` +
        `puntos.</li>`
    );
    li.push(
      `<li><span class="k k-a">⚓</span> <strong>ancla</strong>: la ` +
        `colocación de esa persona se fijó a mano, no la decidió el sistema.</li>`
    );
  }
  if (modes.has("binary")) {
    li.push(
      `<li>El recuadro punteado es una <strong>pierna libre</strong>: ahí no ` +
        `hay nadie todavía.</li>`
    );
    li.push(
      `<li>Tercer renglón en el árbol binario: <strong>«de:» es el ` +
        `patrocinador</strong>, la persona de quien es ese miembro. Un ` +
        `<strong>*</strong> ahí significa que quien le vendió no es su ` +
        `patrocinador.</li>`
    );
    li.push(
      `<li>Segundo renglón: el <strong>paquete</strong> (GTR Go, GTR Fly, ` +
        `GTR Win, o «sin paquete»).</li>`
    );
  }
  // EL PASE EXPLICADO EN LENGUAJE DE NEGOCIO, no con el nombre del campo. Es lo
  // que no se entiende mirando la tabla, y quien recibe el fichero no ha visto
  // la consola ni sabe qué es un `isPassUp`.
  if (modes.has("cmmi")) {
    li.push(
      `<li><span class="k k-p">↑</span> <strong>pase</strong> (pass-up): esa ` +
        `persona <strong>no cuelga de quien la patrocinó</strong>. Su ` +
        `patrocinador no calificaba para recibirla, así que subió a alguien de ` +
        `más arriba de la cadena — y es ése quien la tiene debajo.</li>`
    );
    li.push(
      `<li>En el árbol del CMMI, <strong>«antes:»</strong> es de quién colgaba ` +
        `esa persona en el CMMI viejo. Sólo aparece cuando ha cambiado.</li>`
    );
  }
  li.push(
    `<li>El número gris bajo una tarjeta es cuánta gente cuelga por debajo ` +
      `de ella.</li>`
  );

  return `<div class="legend"><strong>Cómo se lee</strong><ul>${li.join(
    ""
  )}</ul></div>`;
}

/**
 * EL FICHERO, CON LOS ÁRBOLES QUE SE LE PASEN.
 *
 * Uno detrás de otro y cada uno con su título, para que Ctrl+P dé un PDF con
 * todos. La leyenda se arma según qué árboles lleve: explicar una pierna libre
 * en un fichero que sólo trae el CMMI sería ruido, y no explicar el pase en uno
 * que sí lo trae lo dejaría ilegible.
 */
export function buildExportHtml(
  sections: DraftExportSection[],
  meta: DraftExportMeta,
  opts: { h1: string; lead: string; docTitle: string; extraMeta?: string[] }
): string {
  const head =
    `<!DOCTYPE html>\n<html lang="es">\n<head>\n` +
    `<meta charset="utf-8">\n` +
    `<meta name="viewport" content="width=device-width, initial-scale=1">\n` +
    `<title>${esc(opts.docTitle)}</title>\n` +
    `<style>${STYLES}</style>\n</head>\n<body>\n`;

  const parts: string[] = [];
  parts.push(`<h1>${opts.h1}</h1>`);
  parts.push(`<p class="lead">${opts.lead}</p>`);

  parts.push(
    `<div class="meta">` +
      `<div><strong>Run:</strong> ${esc(meta.runLabel || "—")}</div>` +
      `<div><strong>Ápice del árbol:</strong> ${esc(meta.root || "—")}</div>` +
      `<div><strong>Fecha de la migración:</strong> ${esc(
        fmtDate(meta.migrationDate)
      )}</div>` +
      `<div><strong>Fichero generado el:</strong> ${esc(
        fmtDate(meta.generatedAt)
      )}</div>` +
      // CUÁNTOS NODOS TIENE CADA ÁRBOL, uno por uno. No es el mismo número: en
      // el binario no entran los que sólo están en el de patrocinio, y el CMMI
      // es otra red con su propio censo. Un total único los taparía.
      sections
        .map(
          (s) =>
            `<div><strong>${esc(s.title)}:</strong> ${s.forest.drawn} de ${
              s.forest.total
            } nodos · ${s.forest.maxDepth} niveles${
              s.forest.mode === "cmmi"
                ? ` · <strong>${s.forest.passUps} ${
                    s.forest.passUps === 1 ? "pase" : "pases"
                  }</strong>`
                : ""
            }</div>`
        )
        .join("") +
      (opts.extraMeta ?? []).map((m) => `<div>${m}</div>`).join("") +
      `</div>`
  );

  // LA LEYENDA. Quien recibe esto no ha visto la consola: cada marca del dibujo
  // se explica aquí o no se entiende.
  parts.push(legendFor(sections));

  parts.push(
    `<p class="note screen-only">Se puede ampliar con el zoom del navegador, ` +
      `buscar un nombre con Ctrl+F y guardarlo en PDF con Ctrl+P.</p>`
  );

  sections.forEach((section, i) => {
    parts.push(
      `<section><h2>${i + 1} · ${esc(section.title)}</h2>` +
        `<p class="blurb">${section.blurb}</p>` +
        `<div class="tree">${renderForest(section.forest)}</div></section>`
    );
  });

  return head + parts.join("\n") + `\n</body>\n</html>\n`;
}

/** El texto de cada árbol, en un solo sitio para que los dos ficheros coincidan. */
export const SECTION_COPY = {
  binary: {
    title: "Árbol binario — dónde queda colocado cada uno",
    blurb:
      "Es la posición, y no se deshace. Aquí se cuelga por derrame: uno puede " +
      "acabar debajo de cualquiera, no necesariamente de quien lo trajo. Por " +
      "eso cada tarjeta dice abajo de quién es.",
  },
  unilevel: {
    title: "Árbol de patrocinio (unilevel) — de quién es cada uno",
    blurb:
      "Aquí cada persona cuelga de quien la patrocina, que es quien cobra por " +
      "ella. La I o la D de cada tarjeta dice a qué pierna de su patrocinador " +
      "le suman sus puntos de rango.",
  },
  cmmi: {
    title: "Árbol del CMMI — la tercera red",
    blurb:
      "Aquí no hay izquierda ni derecha: cada uno cuelga de uno solo. Lo que " +
      "hay que mirar son los <strong>pases</strong>: los nodos marcados con " +
      "una flecha morada no cuelgan de quien los patrocinó, sino de alguien de " +
      "más arriba, porque su patrocinador no calificaba para recibirlos.",
  },
};

/**
 * LOS DOS ÁRBOLES DEL DRAFT DE COLOCACIÓN, EXPANDIDOS, EN UN HTML AUTOSUFICIENTE.
 *
 * Devuelve la cadena del fichero. No escribe nada y no toca el DOM: quien la
 * convierte en descarga es el componente.
 */
export function buildDraftExportHtml(
  draft: MigrationDraft,
  meta: DraftExportMeta
): string {
  const extra: string[] = [
    `<strong>${draft.nodes?.length ?? 0} miembros</strong> en el borrador` +
      (draft.unplaceable?.length
        ? ` · <strong>${draft.unplaceable.length} SIN COLOCAR</strong>, que no salen en ningún árbol`
        : "") +
      (draft.warnings?.length
        ? ` · ${draft.warnings.length} aviso(s) registrados en la consola`
        : ""),
  ];

  return buildExportHtml(
    [
      { ...SECTION_COPY.binary, forest: buildDraftForest(draft, "binary") },
      { ...SECTION_COPY.unilevel, forest: buildDraftForest(draft, "unilevel") },
    ],
    meta,
    {
      docTitle: `Colocación propuesta — borrador ${meta.runLabel || ""}`.trim(),
      h1: "Colocación propuesta — BORRADOR, todavía sin aplicar",
      lead:
        "Así quedarían los dos árboles de la red si se aplicara este " +
        "borrador. <strong>Nada de esto está escrito todavía</strong>: es " +
        "justo lo que hay que revisar antes de aplicarlo.",
      extraMeta: extra,
    }
  );
}

/**
 * EL ÁRBOL DEL CMMI, EN SU PROPIO FICHERO — y va aparte por una razón dura.
 *
 * El paso del CMMI (`build-cmmi--c`) DEPENDE del de los árboles: el CMMI se
 * calcula caminando la cadena de patrocinio, así que necesita el unilevel ya
 * resuelto. Nunca hay tres borradores vivos a la vez, y un fichero que
 * pretendiera juntar los tres borradores estaría cosiendo dos revisiones de
 * momentos distintos. Cada revisión, su fichero.
 */
export function buildMmiExportHtml(
  draft: MigrationMmiDraft,
  meta: DraftExportMeta
): string {
  const extra: string[] = [
    `Capítulo: <strong>${esc(draft.chapter || "—")}</strong>` +
      (draft.unplaceable?.length
        ? ` · <strong>${draft.unplaceable.length} SIN COLOCAR</strong>, que no salen en el árbol`
        : "") +
      (draft.warnings?.length
        ? ` · ${draft.warnings.length} aviso(s) registrados en la consola`
        : ""),
  ];

  return buildExportHtml(
    [{ ...SECTION_COPY.cmmi, forest: buildMmiForest(draft) }],
    meta,
    {
      docTitle: `CMMI propuesto — borrador ${meta.runLabel || ""}`.trim(),
      h1: "Árbol del CMMI propuesto — BORRADOR, todavía sin aplicar",
      lead:
        "Así quedaría la red del CMMI si se aplicara este borrador. " +
        "<strong>Nada de esto está escrito todavía.</strong> Lo que hay que " +
        "revisar son los <strong>pases</strong>: quién quedó debajo de quién " +
        "cuando el patrocinador no calificaba.",
      extraMeta: extra,
    }
  );
}
